"use client";

import { apiAddComment } from "@/apis/offers";
import { emptyStar, filledStar, FontAwesomeIcon, faPaperPlane } from "@/constants/icons";
import React, { useEffect, useState } from "react";
import { toast, ToastContainer } from "react-toastify";

interface RatingProps {
    offer_id: string;
    company_id: string;
}

const RateUs = ({ offer_id, company_id }: RatingProps) => {
    const [rating, setRating] = useState<number>(0);
    const [comment, setComment] = useState<string>("");
    const [loading, setLoading] = useState<boolean>(false);
    const [hasRated, setHasRated] = useState(false);

    useEffect(() => {
        const lastRated = localStorage.getItem(`hasRated_${offer_id}`);
        if (lastRated && new Date().getTime() - parseInt(lastRated) < 86400000) {
            setHasRated(true);
        }
    }, [offer_id]);

    const handleRate = async (e: React.FormEvent) => {
        e.preventDefault();
        if (hasRated) {
            toast.error("You have already rated, please try again later!", { autoClose: 2000 });
            return;
        }
        handleSubmit();
        localStorage.setItem(`hasRated_${offer_id}`, new Date().getTime().toString());
        setHasRated(true);
    };

    const handleStarClick = (index: number) => {
        setRating(index + 1);
    };

    const handleSubmit = async (e?: React.FormEvent) => {
        e?.preventDefault();

        if (!rating && !comment.trim()) {
            toast.error("Please select a rating or add a comment.", { autoClose: 2000 });
            return;
        }

        setLoading(true);

        try {
            const response = await apiAddComment(offer_id, company_id, comment, rating.toString());

            if (response.status === "success" || (response as any).status === 200) {
                toast.success("Thank you for your feedback!", { autoClose: 2000 });
                setRating(0);
                setComment("");
            } else {
                toast.error("An error occurred. Please try again later!", { autoClose: 2000 });
            }
        } catch (error) {
            toast.error("An error occurred while submitting feedback.", { autoClose: 2000 });
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="bg-white rounded-xl border border-[#E5E7EB] p-6 shadow-sm">
            <ToastContainer />
            <h4 className="text-lg font-bold text-[#1E3A5F] mb-5">Rate This Product</h4>
            
            <form onSubmit={handleRate} autoComplete="off" className="space-y-5">
                {/* Star Rating Section */}
                <div className="space-y-3">
                    <label className="text-sm font-semibold text-[#111827] block">Your Rating</label>
                    <div className="flex items-center gap-2">
                        {[...Array(5)].map((_, index) => (
                            <button
                                type="button"
                                key={index}
                                onClick={() => handleStarClick(index)}
                                className="focus:outline-none transform transition-transform hover:scale-110 active:scale-95"
                            >
                                <FontAwesomeIcon
                                    icon={index < rating ? filledStar : emptyStar}
                                    className={`w-7 h-7 ${index < rating ? 'text-[#F97316]' : 'text-[#E5E7EB]'} transition-colors duration-200`}
                                />
                            </button>
                        ))}
                    </div>
                    <p className="text-xs font-medium text-[#4B5563]">
                        {rating ? `${rating} out of 5 stars` : "Click to rate"}
                    </p>
                </div>

                {/* Comment Section */}
                <div className="space-y-2">
                    <label htmlFor="comment" className="text-sm font-semibold text-[#111827] block">
                        Your Review (Optional)
                    </label>
                    <textarea
                        id="comment"
                        placeholder="Share your experience with this product..."
                        className="w-full px-4 py-3 rounded-lg bg-[#F8FAFC] border border-[#E5E7EB] focus:bg-white focus:border-[#F97316] focus:ring-2 focus:ring-[#FFF7ED] transition-all duration-300 resize-none h-24 text-sm text-[#111827] outline-none placeholder:text-[#4B5563]"
                        value={comment}
                        onChange={(e) => setComment(e.target.value)}
                    />
                </div>

                {/* Submit Button */}
                <button 
                    type="submit" 
                    disabled={loading || (hasRated && rating === 0 && comment === "")}
                    className="w-full py-3 rounded-lg bg-[#F97316] text-white text-sm font-bold hover:bg-[#EA580C] transition-all duration-300 shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 group"
                >
                    {loading ? (
                        "Submitting..."
                    ) : (
                        <>
                            <span>Submit Review</span>
                            <FontAwesomeIcon icon={faPaperPlane} className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                        </>
                    )}
                </button>
            </form>
        </div>
    );
};

export default RateUs;
